import { getCurrencySymbol, AsyncPipe, LowerCasePipe } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { RootState } from '@next/core-lib/app';
import { LocaleService, TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { SelectedCustomer, SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { EnergySystem } from '../../../settings/models/settings.model';
import { getEnergySetting } from '../../../settings/store/selectors/settings.selectors';
import {
  FeedAdvice,
  FeedAdviceType,
  FeedingType,
  InputSystemType,
  ProductTotals,
  SchemeTableRow,
  TableScheme,
} from '../../models/feed-advice.model';
import { FeedAdviceService } from '../../services/feed-advice.service';
import { SchemeSupportService } from '../../services/scheme-support.service';

@Component({
  selector: 'app-feed-advice-report',
  templateUrl: './feed-advice-report.component.html',
  styleUrls: ['./feed-advice-report.component.scss'],
  imports: [AsyncPipe, LowerCasePipe, TranslatePipe, AgrNumberPipe],
})
export class FeedAdviceReportComponent {
  feedAdvice = input<FeedAdvice>();
  tableScheme = input<TableScheme>();
  currency: string;

  customer$: Observable<SelectedCustomer> = this.store.select(UserSelectors.selectCustomer);
  location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);
  energySetting$: Observable<EnergySystem> = this.store.select(getEnergySetting);

  public readonly FeedingType = FeedingType;
  readonly productTotals = computed<ProductTotals[]>(() => {
    const feedAdvice = this.feedAdvice();
    if (!feedAdvice) {
      return [];
    }

    return this.feedAdviceService.calculateProductTotals(feedAdvice);
  });
  readonly grandTotalNettoPrice = computed(() =>
    this.productTotals().reduce((sum, current) => sum + current.totalNettoPrice, 0),
  );
  readonly schemeTableRows = computed<SchemeTableRow[]>(() => {
    const tableScheme = this.tableScheme();
    if (!tableScheme) {
      return [];
    }

    return this.schemeSupportService.calculateTableRows(
      tableScheme,
      InputSystemType.ENERGYDAY,
      this.feedAdvice()?.feedingType,
    );
  });
  readonly hasNettoPrices = computed(() =>
    this.productTotals().some((productTotal) => productTotal.totalNettoPrice > 0),
  );
  readonly isWeanedPigletAdvice = computed(
    () => this.feedAdvice()?.type === FeedAdviceType.WEANED_PIGLETS,
  );
  readonly isRearingGiltAdvice = computed(() => this.feedAdvice()?.type === FeedAdviceType.REARING);
  readonly isFatteningAdvice = computed(() => this.feedAdvice()?.type === FeedAdviceType.FATTENING);
  readonly genders = computed(() => {
    const feedAdvice = this.feedAdvice();

    return feedAdvice
      ? [
          feedAdvice.isBarrow && this.locale.translate('gender.barrow'),
          feedAdvice.isGilt && this.locale.translate('gender.gilt'),
          feedAdvice.isBoar && this.locale.translate('gender.boar'),
        ]
          .filter(Boolean)
          .join(', ')
      : '';
  });

  constructor(
    private readonly store: Store<RootState>,
    private readonly locale: LocaleService,
    private readonly schemeSupportService: SchemeSupportService,
    private readonly feedAdviceService: FeedAdviceService,
  ) {
    this.currency = getCurrencySymbol(this.locale.getCurrency(), 'narrow');
  }
}
